import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Building2,
  UserCheck,
  KeyRound,
  UserX,
  Pencil,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { SearchInput } from "@/components/kit/SearchInput";
import { FilterShell } from "@/components/kit/FilterShell";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { DataTable } from "@/components/tables/DataTable";
import { RowActions } from "@/components/tables/RowActions";
import { EmptyState } from "@/components/feedback/EmptyState";
import { TableSkeleton } from "@/components/feedback/Skeletons";
import { ConfirmDialog } from "@/components/feedback/ConfirmDialog";
import { ExportButton } from "@/components/kit/ExportButton";
import { InviteLinkDialog } from "@/components/forms/InviteLinkDialog";
import { ClientDrawer } from "./ClientDrawer";
import { clientsService, vaAgenciesService, warehousesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";
import { LOGIN_STATUS_LABEL, LOGIN_STATUS_TONE } from "@/lib/constants";
import { useAuth } from "@/hooks/useAuth";
import { canWrite, isOwnerRole } from "@/lib/permissions/permissions";

const DEFAULT_FILTERS = { search: "", status: "all", login: "all", agency: "all" };

export default function ClientsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const writable = canWrite(user?.role, "clients");
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [invite, setInvite] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const params = useMemo(
    () => ({
      search: applied.search,
      status: applied.status === "all" ? "" : applied.status,
      login: applied.login === "all" ? "" : applied.login,
      va_agency_id: applied.agency === "all" ? "" : applied.agency,
    }),
    [applied]
  );

  const listQuery = useQuery({ queryKey: ["clients", params], queryFn: () => clientsService.list(params) });
  const summaryQuery = useQuery({ queryKey: ["clients", "summary"], queryFn: clientsService.summary });
  const agenciesQuery = useQuery({ queryKey: ["va-agencies", "lite"], queryFn: () => vaAgenciesService.list(), enabled: writable });
  const isOwner = isOwnerRole(user?.role);
  const warehousesQuery = useQuery({
    queryKey: ["warehouses", "lite"],
    queryFn: () => warehousesService.list(),
    enabled: isOwner,
  });
  const warehouses = useMemo(() => warehousesQuery.data?.items || [], [warehousesQuery.data]);

  const agencies = useMemo(() => agenciesQuery.data?.items || [], [agenciesQuery.data]);
  const agencyMap = useMemo(
    () => Object.fromEntries(agencies.map((a) => [a.id, a.name])),
    [agencies]
  );

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["clients"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
  };

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setDrawerOpen(true);
  };

  const handleSaved = (res) => {
    refresh();
    if (res?.invite_link) setInvite({ link: res.invite_link, name: res.client?.client_name, email: res.user?.email || res.client?.email });
  };

  const confirmDelete = async () => {
    try {
      await clientsService.remove(toDelete.id);
      toast.success("Client deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setToDelete(null);
    }
  };

  const s = summaryQuery.data || {};
  const rows = listQuery.data?.items || [];

  const columns = [
    {
      key: "client_name",
      header: "Client",
      render: (r) => (
        <div>
          <p className="font-medium text-foreground">{r.client_name}</p>
          {r.company_name && <p className="text-xs text-muted-foreground">{r.company_name}</p>}
        </div>
      ),
    },
    { key: "email", header: "Email", render: (r) => r.email || <span className="text-muted-foreground">—</span> },
    {
      key: "login",
      header: "Login",
      render: (r) => (
        <StatusBadge label={LOGIN_STATUS_LABEL[r.login_status]} tone={LOGIN_STATUS_TONE[r.login_status]} />
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <StatusBadge label={r.status === "active" ? "Active" : "Inactive"} tone={r.status === "active" ? "green" : "neutral"} />
      ),
    },
    {
      key: "agency",
      header: "VA Agency",
      render: (r) => agencyMap[r.assigned_va_agency_id] || <span className="text-muted-foreground">—</span>,
    },
    { key: "created", header: "Created", render: (r) => formatRelativeTime(r.created_at) },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) => (
        writable ? (
          <RowActions
            items={[
              { key: "edit", label: "Edit", icon: Pencil, onSelect: () => openEdit(r) },
              { key: "delete", label: "Delete", icon: Trash2, destructive: true, separatorBefore: true, onSelect: () => setToDelete(r) },
            ]}
          />
        ) : null
      ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="page-clients">
      <PageHeader
        sticky
        title="Clients"
        description="Manage the brands and businesses you fulfill for."
        actions={
          <div className="flex items-center gap-2">
            <ExportButton filename="clients" rows={rows} testId="clients-export" columns={[
              { label: "Client", key: "client_name" },
              { label: "Company", key: "company_name" },
              { label: "Email", key: "email" },
              { label: "Login", get: (r) => LOGIN_STATUS_LABEL[r.login_status] || "" },
              { label: "Status", key: "status" },
              { label: "VA Agency", get: (r) => agencyMap[r.assigned_va_agency_id] || "" },
              { label: "Created", key: "created_at" },
            ]} />
            {writable && (
              <Button className="gap-2" onClick={openCreate} data-testid="clients-primary-action">
                <Plus className="h-4 w-4" />
                New Client
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-total-clients" label="Total Clients" value={s.total ?? "—"} icon={Building2} tone="blue" />
        <StatCard testId="stat-active-clients" label="Active Clients" value={s.active ?? "—"} icon={UserCheck} tone="green" />
        <StatCard testId="stat-clients-with-login" label="With Login" value={s.with_login ?? "—"} icon={KeyRound} tone="amber" />
        <StatCard testId="stat-clients-without-login" label="Without Login" value={s.without_login ?? "—"} icon={UserX} tone="neutral" />
      </div>

      <FilterShell
        onApply={() => {
          setApplied(draft);
          toast.success("Filters applied");
        }}
        onReset={() => {
          setDraft(DEFAULT_FILTERS);
          setApplied(DEFAULT_FILTERS);
          toast("Filters reset");
        }}
      >
        <SearchInput
          value={draft.search}
          onChange={(v) => setDraft((d) => ({ ...d, search: v }))}
          placeholder="Search clients…"
        />
        <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={draft.login} onValueChange={(v) => setDraft((d) => ({ ...d, login: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-login"><SelectValue placeholder="Login" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All login</SelectItem>
            <SelectItem value="yes">With login</SelectItem>
            <SelectItem value="no">Without login</SelectItem>
          </SelectContent>
        </Select>
        <Select value={draft.agency} onValueChange={(v) => setDraft((d) => ({ ...d, agency: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-agency"><SelectValue placeholder="VA Agency" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All agencies</SelectItem>
            {agencies.map((a) => (
              <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterShell>

      {listQuery.isLoading ? (
        <TableSkeleton rows={6} cols={6} />
      ) : (
        <DataTable
          testId="clients-table"
          paginated
          columns={columns}
          data={rows}
          emptyState={
            <EmptyState
              icon={Building2}
              title="No clients yet"
              description="Add your first client to start onboarding their stores and inventory."
              testId="clients-empty-state"
              action={
                writable ? (
                  <Button className="gap-2" onClick={openCreate} data-testid="clients-empty-action">
                    <Plus className="h-4 w-4" /> New Client
                  </Button>
                ) : null
              }
            />
          }
        />
      )}

      <ClientDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        record={editing}
        agencies={agencies}
        warehouses={warehouses}
        onSaved={handleSaved}
      />
      <InviteLinkDialog
        open={!!invite}
        onOpenChange={(o) => !o && setInvite(null)}
        link={invite?.link}
        name={invite?.name}
        email={invite?.email}
      />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete client?"
        description={`This permanently removes "${toDelete?.client_name}" and is recorded in the Activity Log. Their stores are not affected.`}
        confirmLabel="Delete client"
        destructive
        onConfirm={confirmDelete}
      />
    </div>
  );
}
